import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  styled,
  Tooltip,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material'

import { FC, MouseEvent, useMemo } from 'react'

import { PreviewTrail } from '../../models/PreviewTrailSystem.model'
import { StatusEnum, StatusLabels } from '../../models/TrailStatusEnum.model'
import { DifficultyIcon } from './DifficultyIcon.component'

// Override the global MuiPaper gradient so preview cards match the flat, rounded surface used in the public app.
const StyledCard = styled(Card)(({ theme }) => ({
  '& .MuiCardActionArea-root': {
    height: '100%',
  },
  background: theme.palette.grey[800],
  borderRadius: 12,
  height: '100%',
}))

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(1),
  paddingBottom: 0,
}))

const StatusLabel = styled(Typography)<TypographyProps>({
  fontSize: 16,
  fontWeight: 600,
  left: '50%',
  lineHeight: '1.5',
  marginTop: '-0.2em',
  position: 'absolute',
  textAlign: 'center',
  top: '50%',
  transform: 'translate(-50%, -50%)',
})

type TrailCardProps = {
  trail: PreviewTrail
}

export const TrailCard: FC<TrailCardProps> = (props) => {
  const theme = useTheme()

  const handleEditClick = (event: MouseEvent) => {
    // The edit button overlays the drill-in action area, so stop propagation to keep the click local to edit.
    event.stopPropagation()
  }

  const isFreezeThaw = useMemo(
    () =>
      props.trail.status?.status !== 0 &&
      props.trail.status?.freezeThawStatus?.status &&
      props.trail.status?.freezeThawStatus?.status > 0,
    [props.trail.status?.status, props.trail.status?.freezeThawStatus?.status],
  )

  return (
    <StyledCard>
      <CardActionArea href={`/trails/${props.trail.id}`}>
        <Stack justifyContent="space-between" width="100%" height="100%">
          <StyledCardContent>
            <Stack alignItems="center" spacing={2}>
              <Stack direction="row" justifyContent="space-between" width="100%">
                <Stack width="100%" minHeight="4.5em">
                  <Typography component="h3" variant="body1">
                    {props.trail.name}
                  </Typography>
                  <Typography component="p" variant="body2">
                    {Math.round(props.trail.distance * 10) / 10} miles
                  </Typography>
                </Stack>
                <DifficultyIcon difficultyId={props.trail.difficultyId} fontSize={18} />
              </Stack>
              {isFreezeThaw ? (
                <Tooltip title="Soil temps are fluctuating above and below the freezing point, leading to variable conditions. Trails may be firm and rideable under 30° F.">
                  <Box position="relative">
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={130}
                      thickness={5}
                      sx={{ color: theme.palette.freezeThaw.main }}
                    />
                    <StatusLabel variant="h6">Freeze Thaw</StatusLabel>
                  </Box>
                </Tooltip>
              ) : (
                <Box position="relative">
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={130}
                    thickness={5}
                    sx={{
                      color: {
                        [-1]: theme.palette.grey[600],
                        0: theme.palette.text.primary,
                        1: theme.palette.trailStatus.dark,
                        2: theme.palette.trailStatus.main,
                        3: theme.palette.trailStatus.light,
                      }[props.trail.status?.status ?? -1],
                    }}
                  />
                  <StatusLabel variant="h6">
                    {StatusLabels[props.trail.status?.status ?? StatusEnum.Unknown]}
                  </StatusLabel>
                </Box>
              )}
            </Stack>
          </StyledCardContent>
          <CardActions sx={{ justifyContent: 'center', pb: 1.5, pt: 1 }}>
            <Box
              position="relative"
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              minHeight={24}
            >
              {!props.trail.active && <Chip label="Inactive" size="small" sx={{ left: 0, position: 'absolute' }} />}
              <Tooltip title="Edit trail">
                <IconButton
                  aria-label="Edit trail"
                  href={`/trails/${props.trail.id}`}
                  onClick={handleEditClick}
                  size="small"
                  sx={(theme) => ({ color: theme.palette.grey[400], position: 'absolute', right: 0 })}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </CardActions>
        </Stack>
      </CardActionArea>
    </StyledCard>
  )
}
TrailCard.displayName = 'TrailCard'
